import { Box, Group, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { IconPlayerTrackNext, IconLock } from '@tabler/icons-react'
import { getBeatTypeColor } from '../../lib/beatColors'

/**
 * Story-wide review timeline shown above the action bar in the STORY panel.
 *
 * Two tiers, honest to the data the player hook holds:
 * - Thread chips for every thread in the story (only thread-level metadata —
 *   name and beat count — is available for threads other than the current one).
 * - Beat dots for the *current* thread, whose beats are actually loaded.
 *
 * The player may revisit any position at or before the live edge (their
 * furthest-reached spot) and never seek ahead. A "skip to current" control
 * snaps them back to the live edge from anywhere in the review history.
 *
 * @param {Object} props
 * @param {Array} props.threads - All story threads (sorted), each with name + beats_count
 * @param {Array} props.beats - Active beats of the current thread
 * @param {number} props.currentThreadIndex - Thread currently being viewed
 * @param {number} props.currentIndex - Beat currently being viewed within that thread
 * @param {number} props.liveThreadIndex - Thread index of the live edge
 * @param {number} props.liveBeatIndex - Beat index of the live edge
 * @param {(threadIndex: number, beatIndex: number) => boolean} props.isReachable
 * @param {(threadIndex: number, beatIndex: number) => void} props.onJump
 * @param {() => void} props.onSkipToCurrent
 */
export function BeatTimeline({
  threads = [],
  beats = [],
  currentThreadIndex,
  currentIndex,
  liveThreadIndex,
  liveBeatIndex,
  isReachable,
  onJump,
  onSkipToCurrent,
}) {
  if (!threads.length) return null

  // Behind the live edge anywhere in the story → offer "skip to current".
  const isBehindLive =
    currentThreadIndex < liveThreadIndex ||
    (currentThreadIndex === liveThreadIndex && currentIndex < liveBeatIndex)

  // Only show thread chips when there's more than one thread to move between.
  const showThreadChips = threads.length > 1

  return (
    <Box
      px="lg"
      py={6}
      style={{
        borderTop: '1px solid rgba(0, 255, 255, 0.06)',
        background: 'rgba(6, 14, 26, 0.55)',
        flexShrink: 0,
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Box style={{ minWidth: 0, flex: 1 }}>
          {showThreadChips && (
            <Group gap={6} wrap="nowrap" mb={beats.length ? 6 : 0} style={{ overflowX: 'auto' }}>
              {threads.map((thread, ti) => {
                const reachable = isReachable(ti, 0)
                const isCurrent = ti === currentThreadIndex
                const label = thread?.name || `Chapter ${ti + 1}`
                return (
                  <Tooltip
                    key={thread?.id ?? ti}
                    label={reachable ? label : 'Not yet reached'}
                    position="top"
                    withinPortal
                  >
                    <UnstyledButton
                      onClick={reachable && !isCurrent ? () => onJump(ti, 0) : undefined}
                      style={{
                        flexShrink: 0,
                        padding: '2px 10px',
                        borderRadius: 3,
                        border: isCurrent
                          ? '1px solid rgba(0, 229, 255, 0.7)'
                          : '1px solid rgba(0, 255, 255, 0.15)',
                        background: isCurrent ? 'rgba(0, 229, 255, 0.14)' : 'transparent',
                        opacity: reachable ? 1 : 0.3,
                        cursor: reachable && !isCurrent ? 'pointer' : 'default',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <Group gap={4} wrap="nowrap">
                        {!reachable && <IconLock size={10} color="rgba(255,255,255,0.4)" />}
                        <Text
                          size="xs"
                          fw={isCurrent ? 700 : 500}
                          ff="Orbitron, sans-serif"
                          c={isCurrent ? 'cyan.3' : 'dimmed'}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {ti + 1}
                        </Text>
                      </Group>
                    </UnstyledButton>
                  </Tooltip>
                )
              })}
            </Group>
          )}

          {/* Beat dots for the current thread */}
          {beats.length > 1 && (
            <Group gap={5} wrap="nowrap" style={{ overflowX: 'auto', paddingBottom: 1 }}>
              {beats.map((beat, bi) => {
                const reachable = isReachable(currentThreadIndex, bi)
                const isCurrent = bi === currentIndex
                const color = getBeatTypeColor(beat.beat_type)
                const isInteractive = beat.beat_type === 'decision' || beat.beat_type === 'input'
                // Prefer the beat's human-readable name; fall back to its index.
                const beatName = beat.name?.trim() || `Beat ${bi + 1}`
                return (
                  <Tooltip
                    key={beat.id}
                    label={
                      reachable
                        ? `${beatName}${isInteractive ? ' — your answer (locked)' : ''}`
                        : 'Not yet reached'
                    }
                    position="top"
                    withinPortal
                  >
                    <UnstyledButton
                      onClick={reachable && !isCurrent ? () => onJump(currentThreadIndex, bi) : undefined}
                      aria-label={`Go to ${beatName}`}
                      style={{
                        flexShrink: 0,
                        width: isCurrent ? 22 : 12,
                        height: 12,
                        borderRadius: 6,
                        border: isCurrent
                          ? `1px solid var(--mantine-color-${color}-4)`
                          : '1px solid rgba(255,255,255,0.18)',
                        background: isCurrent
                          ? `var(--mantine-color-${color}-5)`
                          : reachable
                            ? `var(--mantine-color-${color}-9)`
                            : 'rgba(255,255,255,0.06)',
                        boxShadow: isCurrent ? `0 0 8px var(--mantine-color-${color}-7)` : 'none',
                        opacity: reachable ? 1 : 0.4,
                        cursor: reachable && !isCurrent ? 'pointer' : 'default',
                        transition: 'all 0.15s ease',
                      }}
                    />
                  </Tooltip>
                )
              })}
            </Group>
          )}
        </Box>

        {isBehindLive && (
          <Tooltip label="Skip to where you left off" position="top" withinPortal>
            <UnstyledButton
              onClick={onSkipToCurrent}
              style={{
                flexShrink: 0,
                padding: '4px 10px',
                borderRadius: 3,
                border: '1px solid rgba(0, 229, 255, 0.4)',
                background: 'rgba(0, 229, 255, 0.1)',
                transition: 'all 0.15s ease',
              }}
            >
              <Group gap={5} wrap="nowrap">
                <Text size="xs" fw={600} ff="Orbitron, sans-serif" c="cyan.3" style={{ whiteSpace: 'nowrap' }}>
                  Current
                </Text>
                <IconPlayerTrackNext size={13} color="rgba(0, 229, 255, 0.9)" />
              </Group>
            </UnstyledButton>
          </Tooltip>
        )}
      </Group>
    </Box>
  )
}
